import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, apiGet, toggleWidgetTheme, updateSelectedSymbolInDropdown, getEChartsTooltipTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML, isEmptyWidgetData } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseFearAndGreedChart {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        return [
            { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
            { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
            { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
            { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
            { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
        ];
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        const symbols = this.fetchSymbols();
        return {
      headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        this.attachStyles();
        this.setupEventListeners();
        await this.setupChart(this.config.symbol!);
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private timeFrame: '30d' | '1y' = '30d';

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
      let dataTheme = 'default-dark';
      
      if (this.config.theme === 'white') {
        themeClass = 'whiteMode';
        dataTheme = 'white';
      } else if (this.config.theme === 'black') {
        themeClass = 'blackMode';
        dataTheme = 'black';
      } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <div class="header-content">
            <h4 class="title crypto">${this.getTitleIconSvg()} Fear And Greed Index Chart</h4>
          </div>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}</div>
        </div>
        <div class="inBody">
          <div class="optionsRow" data-testing-id="options-row">
            <div class="item" data-testing-id="legends-container">
                <div class="legendsCollection" data-testing-id="legends-collection">
                    <span class="toggle-series" data-tooltip="5Y" style="background-color:#10B981;" data-testing-id="legend-5y"></span>
                    <span class="toggle-series" data-tooltip="10Y" style="background-color:#7C3AED;" data-testing-id="legend-10y"></span>
                </div>
            </div>
            <span></span>
            <div class="item tabsDv padHt" data-testing-id="tabs-container">
                <button class="tab-btn active" data-timeframe="30d" data-testing-id="tab-30d">30 Days</button>
                <button class="tab-btn" data-timeframe="1y" data-testing-id="tab-1y">1 Year</button>
            </div>
          </div>
          <div class="chartDv" id="PriceCamprisonContainer" style="height:400px" data-testing-id="price-comparison-container"></div>
        </div>
      </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
        const tabs = this.shadowRoot.querySelectorAll('.tab-btn');
        tabs.forEach(tab => {
            tab.addEventListener('click', (e) => {
                const target = e.target as HTMLElement;
                const timeframe = target.getAttribute('data-timeframe') as '30d' | '1y';

                tabs.forEach(t => t.classList.remove('active'));
                target.classList.add('active');

                this.timeFrame = timeframe;
                this.setupChart(this.config.symbol);
            });
        });
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            const existing = document.querySelector('script[data-chart-lib]');
            if (existing) {
                existing.addEventListener('load', () => resolve(true));
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                document.head.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(symbol?: string, info?: any): Promise<void> {
        try {
            const response = await apiGet({
                method: 'GET',
                url: `${APIURL}/v1/crypto/fear_greed_index?interval=${this.timeFrame}`,
                body: { interval: this.timeFrame },
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error("Failed to fetch data");
            this.currentData = await response.json();

            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
        } catch (err) {
            console.error("Error fetching chart data:", err);
        }
    }

    private updateDisplay(): void {
        const container = this.shadowRoot.querySelector('#PriceCamprisonContainer') as HTMLElement;
        if (!(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        if (!this.currentData || isEmptyWidgetData(this.currentData.data?.all)) {
            if (container) container.innerHTML = getNoDataHTML();
            return;
        }
        this.renderChart();
    }

    private renderChart(): void {
        const container = this.shadowRoot.querySelector('#PriceCamprisonContainer');
        if (!container) return;

        container.innerHTML = `<div id="marketRealtimeChart" style="width:100%;height:100%;"></div>`;
            const hasData =
        this.currentData?.data?.all &&
        this.currentData.data.all.length > 0;

    if (hasData) {
        (container as HTMLElement).setAttribute('data-testing-id', 'fear-greed-chart');
    } else {
        (container as HTMLElement).removeAttribute('data-testing-id');
    }

        this.drawChart();
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const chartContainer = this.shadowRoot.querySelector('#marketRealtimeChart') as HTMLElement;
        if (!chartContainer || !this.currentData?.data?.all) return;

        const allData = this.currentData.data.all;

        const fearGreedData = allData
            .filter((d: any) => d.data_type === 'fear_and_greed')
            .sort((a: any, b: any) => a.timestamp_unix - b.timestamp_unix);

        const btcData = allData
            .filter((d: any) => d.data_type === 'btc_ohlcv')
            .sort((a: any, b: any) => a.timestamp_unix - b.timestamp_unix);

        const sortedReferenceData = fearGreedData.length ? fearGreedData : btcData;

        const xData = sortedReferenceData.map((d: any) =>
            new Date(d.timestamp_unix * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        );

        const fearGreedValues = fearGreedData.map((d: any) => d.value);
        const btcPriceValues = btcData.map((d: any) => d.quote?.USD?.close ?? 0);
        const btcVolumeValues = btcData.map((d: any) => d.quote?.USD?.volume ?? 0);

        const instance = echarts.getInstanceByDom(chartContainer);
        if (instance) echarts.dispose(instance);

        const chart = echarts.init(chartContainer);
        const isWhite = this.config.theme === 'white';

        // const TEXT_COLOR = isWhite ? '#222' : '#ccc';
        // const GRID_LINE_COLOR = isWhite ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)';
        const BACKGROUND_COLOR = 'transparent';

        const FNG_COLOR = isWhite ? '#e6b800' : '#FFD700';
        const BTC_PRICE_COLOR = isWhite ? '#6a0dad' : '#9370DB';
        const BTC_VOLUME_COLOR = isWhite ? '#555' : '#808080';

        const formatNumber = (value: number) => {
            if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
            if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
            if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
            return value.toString();
        };

        // const isBlack = this.config.theme === 'black';
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
        const axisLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-axis-color');
        const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');

        const option = {
            backgroundColor: BACKGROUND_COLOR,
            tooltip: {
                trigger: 'axis',
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (params: any) => {
                    let tooltipText = `${params[0].axisValue}<br/>`;
                    params.forEach((item: any) => {
                        let value = item.seriesName === 'Bitcoin Price' || item.seriesName === 'Bitcoin Volume'
                            ? formatNumber(item.value)
                            : item.value;
                        tooltipText += `<span style="color:${item.color}">\u25CF</span> ${item.seriesName}: ${value}${item.seriesName === 'Bitcoin Price' ? '$' : ''}<br/>`;
                    });
                    return tooltipText;
                }
            },
            xAxis: {
                type: 'category',
                data: xData,
                axisLine: { lineStyle: { color: axisLineColor } },
                axisLabel: { color: textColor, fontSize: fontSize, }
            },
            yAxis: [
                {
                    type: 'value',
                    position: 'left',
                    axisLine: { lineStyle: { color: axisLineColor } },
                    splitLine: { lineStyle: { color: splitLineColor } },
                    axisLabel: {
                        color: textColor, fontSize: fontSize,
                        formatter: (value: number) => `$${formatNumber(value)}`
                    }
                },
                {
                    type: 'value',
                    position: 'left',
                    min: 0,
                    max: 100,
                    axisLine: { lineStyle: { color: axisLineColor } },
                    splitLine: { show: false },
                    axisLabel: { color: textColor, fontSize: fontSize, }
                }
            ],
            grid: { left: 0, right: 0, top: 10, bottom: 0, containLabel: true },
            series: [
                {
                    name: 'Crypto Greed and Fear Index',
                    type: 'line',
                    yAxisIndex: 1,
                    smooth: true,
                    data: fearGreedValues,
                    lineStyle: { color: FNG_COLOR, width: 2 },
                    itemStyle: { color: FNG_COLOR },
                    showSymbol: false,
                    markArea: {
                        silent: true,
                        data: [
                            [{ yAxis: 80, name: 'Extreme Greed', itemStyle: { color: 'rgba(0, 128, 0, 0.4)' }, label: { position: 'insideRight', align: 'right', color: textColor, fontWeight: 'bold' } }, { yAxis: 100 }],
                            [{ yAxis: 50, name: 'Greed', itemStyle: { color: 'rgba(0, 128, 0, 0.2)' }, label: { position: 'insideRight', align: 'right', color: textColor, fontWeight: 'bold' } }, { yAxis: 80 }],
                            [{ yAxis: 40, name: 'Neutral', itemStyle: { color: 'rgba(128, 128, 0, 0.1)' }, label: { position: 'insideRight', align: 'right', color: textColor, fontWeight: 'bold' } }, { yAxis: 50 }],
                            [{ yAxis: 20, name: 'Fear', itemStyle: { color: 'rgba(255, 69, 0, 0.2)' }, label: { position: 'insideRight', align: 'right', color: textColor, fontWeight: 'bold' } }, { yAxis: 40 }],
                            [{ yAxis: 0, name: 'Extreme Fear', itemStyle: { color: 'rgba(139, 0, 0, 0.4)' }, label: { position: 'insideRight', align: 'right', color: textColor, fontWeight: 'bold' } }, { yAxis: 20 }]
                        ]
                    }
                },
                {
                    name: 'Bitcoin Price',
                    type: 'line',
                    yAxisIndex: 0,
                    smooth: true,
                    data: btcPriceValues,
                    lineStyle: { color: BTC_PRICE_COLOR, width: 1 },
                    itemStyle: { color: BTC_PRICE_COLOR },
                    showSymbol: false
                },
                {
                    name: 'Bitcoin Volume',
                    type: 'line',
                    yAxisIndex: 0,
                    smooth: true,
                    data: btcVolumeValues,
                    lineStyle: { color: BTC_VOLUME_COLOR, width: 1, type: 'dashed' },
                    itemStyle: { color: BTC_VOLUME_COLOR },
                    showSymbol: false
                }
            ]
        };

        chart.setOption(option);
        this.currentChart = chart;
        
        // Add data-testing-id to chart after render
        setTimeout(() => {
            const chartSvg = this.shadowRoot.querySelector('#marketRealtimeChart svg');
            if (chartSvg) {
                chartSvg.setAttribute('data-testing-id', 'fear-greed-chart-svg');
            }
            const legendItems = this.shadowRoot.querySelectorAll('.legend-item');
            legendItems.forEach((item, idx) => {
                const seriesName = idx === 0 ? 'fng' : idx === 1 ? 'btc-price' : 'btc-volume';
                item.setAttribute('data-testing-id', `legend-item-${seriesName}`);
                item.setAttribute('data-series', seriesName);
            });
        }, 500);
    }

    private changeSymbol(stockId: string): void {
        changeSymbolForWidget(
            this.shadowRoot,
            this.config,
            { symbol: stockId },
            stockId,
            (id: any, info: any) => this.setupChart(id, info),
            (type, data) => this.emit(type, data)
        );
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;
      
        this.config.theme = toggleWidgetTheme(
          this.shadowRoot,
          theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
      
        this.updateDisplay();
    }

    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M15.15 20.3C12.3125 20.3 10 17.9875 10 15.15C10 12.3125 12.3125 10 15.15 10C17.9875 10 20.3 12.3125 20.3 15.15C20.3 17.9875 18 20.3 15.15 20.3ZM15.15 11.8625C13.3375 11.8625 11.875 13.3375 11.875 15.1375C11.875 16.9375 13.35 18.4125 15.15 18.4125C16.95 18.4125 18.425 16.9375 18.425 15.1375C18.425 13.3375 16.9625 11.8625 15.15 11.8625Z" fill="currentColor"/> <path d="M15 28.4375C7.5875 28.4375 1.5625 22.4125 1.5625 15C1.5625 7.5875 7.5875 1.5625 15 1.5625C22.4125 1.5625 28.4375 7.5875 28.4375 15C28.4375 22.4125 22.4125 28.4375 15 28.4375ZM15 3.4375C8.625 3.4375 3.4375 8.625 3.4375 15C3.4375 21.375 8.625 26.5625 15 26.5625C21.375 26.5625 26.5625 21.375 26.5625 15C26.5625 8.625 21.375 3.4375 15 3.4375Z" fill="currentColor"/> <path d="M15.0625 22C14.5158 22 14.0625 21.5192 14.0625 20.9394V9.06061C14.0625 8.48081 14.5158 8 15.0625 8C15.6092 8 16.0625 8.48081 16.0625 9.06061V20.9394C16.0625 21.5192 15.6092 22 15.0625 22Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseFearAndGreedChart: typeof TraderverseFearAndGreedChart; } }
if (typeof window !== 'undefined') { window.TraderverseFearAndGreedChart = TraderverseFearAndGreedChart; }